import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, inject, NgZone, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { PopoverModule } from 'primeng/popover';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { EntityDialogData } from '../../../services/entity-dialog.service';
import { UnitConversionHelperService } from '../../../services/unit-conversion-helper.service';
import { UnitConversionDomainStore } from '../../../stores/unit-conversion.store';
import { EntityDefinition } from '../../../types/entity-definition.interface';
import { UnitConversionDomain } from '../../../types/unit-conversion.interface';
import { UnitType } from '../../../types/unit-type.interface';
import { Unit } from '../../../types/unit.interface';
import { formatUnitLabel } from '../../../utils/unit-select.utils';
import { CesDeleteButtonComponent, CesMenuButtonSecondaryComponent } from '../../ui/ces-buttons';
import { CesDialogFooterComponent } from '../../ui/dialog-footer/dialog-footer.component';
import { CesIconComponent } from '../../ui/ces-icon/ces-icon.component';
import { CesTextFieldComponent } from '../../ui/ces-text-field/ces-text-field.component';
import { buildDeleteConfig } from '../../../utils/delete-confirmation';

import { CesTooltipAutoDismissDirective } from '../../../directives/ces-tooltip-auto-dismiss.directive';
interface UnitDialogAdditionalData {
  UnitType?: UnitType[];
  Units?: Unit[];
}

interface UnitDialogData extends Omit<EntityDialogData<Unit>, 'additionalData'> {
  additionalData?: UnitDialogAdditionalData;
}

type UnitConversionStoreApi = {
  loadByUnitId(srcUnitId: number): Promise<UnitConversionDomain[]>;
  entities(): UnitConversionDomain[];
};

@Component({
  selector: 'app-unit-form',
  imports: [
    CesTooltipAutoDismissDirective,
    InputTextModule,
    SelectModule,
    FormsModule,
    MenuModule,
    PopoverModule,
    CesIconComponent,
    CommonModule,
    CesTextFieldComponent,
    CesDeleteButtonComponent,
    CesDialogFooterComponent,
    CesMenuButtonSecondaryComponent,
    TranslocoModule,
    TooltipModule,
  ],
  templateUrl: './unit-form.component.html',
  styleUrl: './unit-form.component.css',
})
export class UnitFormComponent implements OnInit, AfterViewInit {
  entityDefinition?: EntityDefinition<Unit>;
  mode: 'create' | 'edit' | 'delete' = 'create';
  additionalData?: UnitDialogAdditionalData;

  unitItems: MenuItem[] = [];

  @ViewChild('nameCtrl') nameCtrl?: NgModel;
  @ViewChild('symbolCtrl') symbolCtrl?: NgModel;
  @ViewChild('unitTypeCtrl') unitTypeCtrl?: NgModel;

  private readonly config = inject<DynamicDialogConfig<UnitDialogData> | null>(DynamicDialogConfig, { optional: true });
  private readonly dialogRef = inject<DynamicDialogRef<UnitFormComponent> | null>(DynamicDialogRef, { optional: true });
  protected readonly unitConversionListStore = inject(UnitConversionDomainStore);
  private readonly conversionHelper = inject(UnitConversionHelperService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private readonly translate = inject(TranslocoService);

  unitTypes: UnitType[] = [];
  unit: Unit = { id: 0, name: '', symbol: '', isUsed: false, unitTypeId: null, type: '' };
  other_units: Unit[] = [];
  unitConversions: UnitConversionDomain[] = [];
  deletedUnitConversions: UnitConversionDomain[] = [];

  private externalErrorsMap: Record<string, Record<string, string>> = {
    name: {},
    symbol: {},
  };
  private initialValidationDone = false;

  // Shared delete-confirmation factory so the unit name renders bold (boldName) like every other entity.
  protected unitDeleteConfig() {
    return buildDeleteConfig(this.entityDefinition?.messages?.confirmDelete?.(), this.unit.name);
  }

  ngOnInit(): void {
    const data = this.config?.data;
    if (!data) {
      return;
    }
    this.entityDefinition = data.entityDefinition;
    const additional = data.additionalData ?? {};
    this.unitTypes = additional.UnitType ?? [];

    if (data.entity) {
      this.unit = { ...data.entity };
      const conversionStore = this.unitConversionListStore as unknown as UnitConversionStoreApi;
      void conversionStore.loadByUnitId(this.unit.id).then(() => {
        // Work on copies: ngModel edits and row additions must never leak into the shared store state.
        this.unitConversions = conversionStore.entities().map(conversion => ({ ...conversion }));
        this.filterUnitForConversion();
      });
    }

    this.other_units = (additional.Units ?? []).filter(o => o.id !== this.unit.id);

    this.mode = data.mode === 'edit' || data.mode === 'create' ? data.mode : 'create';
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.nameCtrl) {
        this.validateUniqueName(this.nameCtrl);
      }
      if (this.symbolCtrl) {
        this.validateUniqueName(this.symbolCtrl);
      }

      this.initialValidationDone = true;
      this.cdr.detectChanges();
    });
  }

  changeUnitType(): void {
    if (this.nameCtrl) this.validateUniqueName(this.nameCtrl);
    if (this.symbolCtrl) this.validateUniqueName(this.symbolCtrl);
    this.filterUnitForConversion();
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    }
  }

  validate(): void {
    this.validateControls();

    if (this.nameCtrl?.invalid || this.symbolCtrl?.invalid || this.unitTypeCtrl?.invalid || this.hasInvalidConversion()) {
      return;
    }

    this.unit.name = this.unit.name.trim();
    this.unit.symbol = this.unit.symbol.trim();
    const result = {
      action: this.mode,
      unit: this.unit,
      unitConversionList: this.unitConversions,
      deletedConversions: this.deletedUnitConversions,
    };
    if (this.dialogRef) this.dialogRef.close(result);
  }

  delete(): void {
    const onDelete = this.config?.data?.onDelete;

    if (typeof onDelete === 'function') {
      onDelete(this.unit);
      if (this.dialogRef) {
        this.dialogRef.close();
      }
    }
  }

  deleteConversion(conversion: UnitConversionDomain): void {
    if (conversion.id !== 0) this.deletedUnitConversions.push(conversion);
    // Compare by reference: every freshly added conversion still carries id 0.
    this.unitConversions = this.unitConversions.filter(o => o !== conversion);
    this.filterUnitForConversion();
  }

  addUnitToConvert(targetUnit: Unit): void {
    const newConversion: UnitConversionDomain = {
      id: 0,
      name: '',
      srcUnitId: this.unit.id,
      srcUnitNameI18n: this.unit.name,
      srcSymbol: this.unit.symbol,
      tgtUnitId: targetUnit.id,
      tgtUnitNameI18n: targetUnit.name,
      tgtSymbol: targetUnit.symbol,
      expression: '',
      formulaId: 0,
      reverseExpression: '',
      reverseFormulaId: 0,
      reverseId: 0,
    };

    this.unitConversions.push(newConversion);
    this.filterUnitForConversion();
  }

  validateUniqueName(control: NgModel): void {
    const errorKey = control?.name ?? '';
    if (!control?.value) {
      // A cleared field falls back to the required error; drop any stale uniqueness message.
      if (errorKey) this.externalErrorsMap[errorKey] = {};
      return;
    }

    const value: unknown = control.value;
    const stringValue = typeof value === 'string' ? value : String(value);
    const normalizedValue = stringValue.toLowerCase().trim();

    // L'unicité suit le contrat API/DB (unit_name_type_uq) : elle s'évalue au sein d'un même type d'unité.
    const sameTypeUnits = this.other_units.filter(unit => unit.unitTypeId === this.unit.unitTypeId);
    const valid =
      control.name === 'symbol'
        ? !sameTypeUnits.some(unit => unit.symbol.toLowerCase().trim() === normalizedValue)
        : !sameTypeUnits.some(unit => unit.name.toLowerCase().trim() === normalizedValue);

    if (!valid) {
      this.externalErrorsMap[errorKey] = {
        uniqueName:
          control.name === 'symbol'
            ? this.translate.translate('validation.duplicateSymbol')
            : this.translate.translate('validation.duplicateName'),
      };

      this.zone.run(() => {
        control.control.setErrors({ uniqueName: true });
      });

      // A uniqueness error can land on a pristine prefilled field (edit mode): without this,
      // the submit button greys out while the message stays hidden behind the touched/dirty gate.
      (control.valueAccessor as { markAsTouched?: () => void } | null)?.markAsTouched?.();
    } else {
      this.externalErrorsMap[errorKey] = {};

      this.zone.run(() => {
        if (control.errors?.['uniqueName'] && Object.keys(control.errors).length === 1) {
          control.control.setErrors(null);
        } else if (control.errors?.['uniqueName']) {
          const errors: Record<string, unknown> = { ...control.errors };
          delete errors['uniqueName'];
          control.control.setErrors(Object.keys(errors).length ? errors : null);
        }
      });
    }

    if (this.initialValidationDone) {
      this.cdr.detectChanges();
    }
  }

  getExternalErrors(control: NgModel): Record<string, string> | undefined {
    if (!control?.name) return undefined;

    return this.externalErrorsMap[control.name] ?? {};
  }

  validateControls(): void {
    if (this.nameCtrl) {
      this.validateUniqueName(this.nameCtrl);
      this.nameCtrl.control.markAsTouched();
    }

    if (this.symbolCtrl) {
      this.validateUniqueName(this.symbolCtrl);
      this.symbolCtrl.control.markAsTouched();
    }

    if (this.unitTypeCtrl) {
      this.unitTypeCtrl.control.markAsTouched();
    }
  }

  private filterUnitForConversion(): void {
    const convertedUnits = this.unitConversions.map(u => u.tgtUnitId);
    const filteredUnits = this.other_units.filter(
      o => o.unitTypeId === this.unit.unitTypeId && o.id !== this.unit.id && !convertedUnits.includes(o.id),
    );

    this.unitItems = filteredUnits.map(unit => ({
      label: formatUnitLabel(unit.name, unit.symbol),
      command: () => this.addUnitToConvert(unit),
    }));
  }

  isFormInvalid(): boolean {
    return [this.nameCtrl, this.symbolCtrl, this.unitTypeCtrl].some(ctrl => ctrl?.invalid === true) || this.hasInvalidConversion();
  }

  /** A conversion needs a usable expression in both directions; X denotes the starting value. */
  isExpressionValid(expression: string): boolean {
    return this.conversionHelper.isExpressionValid(expression);
  }

  private hasInvalidConversion(): boolean {
    return this.unitConversions.some(c => !this.isExpressionValid(c.expression) || !this.isExpressionValid(c.reverseExpression));
  }
}
